import Flex from 'antd/lib/flex'
import { useTaskContext } from 'Contexts/TaskContext'
import SelectNodeIndicator from 'src/assets/icons/select-something-indicator.svg?react'
import styles from './JSONAnnotationTask.module.less'
import DashedLine from 'src/assets/icons/dashed-line.svg?react'
import { CloseOutlined } from '@ant-design/icons'
import { Fragment, useMemo } from 'react'
import { getMetadataForPath } from '../utils'
import { JSONPath } from 'jsonpath-plus'
import { JSONObject, JSONValue, SchemaStringFormatType } from 'Types/Common.types'
import { PromptHTMLContent } from 'Components/tasks/Prompt/PromptContentTypes/PromptHTMLContent'
import { PromptMarkdownContent } from 'Components/tasks/Prompt/PromptContentTypes/PromptMarkdownContent'
import { DEFAULT_SELECTED_PATH } from 'Constants/attributes.constants'
import NestedJSONContent from 'src/assets/icons/nested-json-content.svg?react'
import isEmpty from 'lodash/isEmpty'

type Props = {
    selectedPath: string
    setSelectedPath: (path: string) => void
    content: JSONObject
}

const AttributesSection = ({
    element,
    selectedPath,
}: {
    element: React.ReactElement
    selectedPath: string
}) => {
    return (
        <Fragment key={selectedPath}>
            <DashedLine />
            <div className={styles['fill-attributes']}>Fill the below attributes</div>
            <div className={styles['fill-attributes-element']}>{element}</div>
        </Fragment>
    )
}

const getDataValueForPath = (data: JSONObject, path: string): JSONValue[] => {
    const result = JSONPath({
        path: path,
        json: data,
    })
    return result
}

const getParentPath = (data: JSONObject, path: string): string => {
    const result = JSONPath({
        path: path,
        json: data,
        resultType: 'parentProperty',
    })
    return result[0]
}

const EmptyState = ({ label, caption }: { label: string; caption: string }) => {
    return (
        <Flex vertical gap={14} align="center" justify="center">
            <SelectNodeIndicator />
            <Flex vertical gap={8} align="center" justify="center">
                <div className={styles['select-node-indicator-label']}>{label}</div>
                <div className={styles['select-node-indicator-caption']}>{caption}</div>
            </Flex>
        </Flex>
    )
}

export const JSONAnnotationContentDetail: React.FC<Props> = ({
    selectedPath,
    setSelectedPath,
    content,
}) => {
    const taskContext = useTaskContext()
    const attributeManager = taskContext.getAttributeManager?.()
    const metadata =
        taskContext.studio.taskConfig.assignment.task.json_schema_metadata?.metadata ?? null

    const getArrayIndexFromPath = (path: string): number | null => {
        const regex = /\[(\d+)\]$/
        const result = regex.exec(path)
        if (result) {
            return Number.parseInt(result[1], 10) + 1
        }
        return null
    }

    const metadataInfo = useMemo(() => {
        const data: {
            label: string
            description: string | null
            format: SchemaStringFormatType | null
            actualValue: JSONValue[]
            arrayIndex: number | null
        } = {
            label: '',
            description: null,
            format: null,
            actualValue: [],
            arrayIndex: null,
        }
        const pathMetadata = getMetadataForPath(metadata, selectedPath)
        if (pathMetadata && !isEmpty(pathMetadata)) {
            data.label = pathMetadata._meta.label ?? ''
            data.description = pathMetadata._meta.description
            data.format = 'format' in pathMetadata._meta ? pathMetadata._meta.format : null
        }
        data.actualValue = getDataValueForPath(content, selectedPath)
        data.arrayIndex = getArrayIndexFromPath(selectedPath)
        if (!data.label) {
            data.label = getParentPath(content, selectedPath)
        }
        return data
    }, [selectedPath, metadata, content])

    const renderFormattedContent = (content: string, format: SchemaStringFormatType | null) => {
        if (!content) return <div style={{ whiteSpace: 'pre-wrap' }}>{content}</div>

        switch (format) {
            case SchemaStringFormatType.Html:
                return <PromptHTMLContent content={content} id={selectedPath} />
            case SchemaStringFormatType.Markdown:
                return (
                    <PromptMarkdownContent
                        content={content}
                        id={selectedPath}
                        allowRawHtml={true}
                    />
                )
            case SchemaStringFormatType.Json: {
                let component: React.ReactElement
                try {
                    const parsed = JSON.parse(content)
                    component = <pre>{JSON.stringify(parsed, null, 2)}</pre>
                } catch (e) {
                    component = <pre>{content}</pre>
                    console.error(e)
                }
                return (
                    <div style={{ whiteSpace: 'pre-wrap' }} data-testid="json-content">
                        {component}
                    </div>
                )
            }
            case SchemaStringFormatType.Text:
            default:
                return (
                    <div style={{ whiteSpace: 'pre-wrap' }} data-testid="text-content">
                        {content}
                    </div>
                )
        }
    }

    const element = attributeManager?.getAttributeGroup(selectedPath)?.getElement()?.element

    if (selectedPath === DEFAULT_SELECTED_PATH && !element) {
        return (
            <Flex
                justify="center"
                align="center"
                className={styles['select-node-indicator-container']}
            >
                <EmptyState
                    label={'Select to view'}
                    caption="Select an entity from left panel to view and fill its attributes"
                />
            </Flex>
        )
    }

    if (selectedPath === DEFAULT_SELECTED_PATH && element) {
        return (
            <Flex vertical gap={20}>
                <EmptyState
                    label={'Fill top level attributes'}
                    caption="Go through each entity and answer the attributes below with an overall assessment."
                />
                <AttributesSection element={element} selectedPath={selectedPath} />
            </Flex>
        )
    }

    const isPrimitive =
        !metadataInfo?.actualValue.length ||
        (metadataInfo?.actualValue.length === 1 && typeof metadataInfo?.actualValue[0] !== 'object')

    if (!isPrimitive && !element) {
        return (
            <Flex
                justify="center"
                align="center"
                className={styles['select-node-indicator-container']}
            >
                <EmptyState
                    label={'Expand and review'}
                    caption="Go through every entity inside it and fill in the relevant attributes."
                />
            </Flex>
        )
    }

    if (!isPrimitive && element) {
        return (
            <Flex vertical gap={20}>
                <Flex justify="space-between" align="flex-start">
                    <NestedJSONContent />
                    <CloseOutlined
                        className={styles['close-icon']}
                        onClick={() => setSelectedPath(DEFAULT_SELECTED_PATH)}
                    />
                </Flex>
                <Flex vertical gap={20}>
                    <div className={styles['selected-node-label']}>
                        {metadataInfo.arrayIndex
                            ? `# ${metadataInfo.arrayIndex}`
                            : metadataInfo.label}
                    </div>
                    <div className={styles['selected-node-description']}>
                        Review each item & provide response for the attributes linked with them.
                    </div>
                    <AttributesSection element={element} selectedPath={selectedPath} />
                </Flex>
            </Flex>
        )
    }

    return (
        <Flex vertical gap={20}>
            <Flex justify="space-between">
                <div className={styles['selected-node-label']}>
                    {metadataInfo.arrayIndex ? `# ${metadataInfo.arrayIndex}` : metadataInfo.label}
                </div>
                <CloseOutlined
                    className={styles['close-icon']}
                    onClick={() => setSelectedPath(DEFAULT_SELECTED_PATH)}
                />
            </Flex>
            <Flex vertical gap={20}>
                <div className={styles['selected-node-description']}>
                    {renderFormattedContent(
                        metadataInfo.actualValue[0] as string,
                        metadataInfo.format
                    )}
                </div>
                {element && <AttributesSection element={element} selectedPath={selectedPath} />}
            </Flex>
        </Flex>
    )
}
